import React, { useState, useEffect } from 'react';
import { Plus, X } from 'lucide-react';
import { useAlert } from './contexts/AlertContext';
import { useStaff } from './contexts/DataProviders';
import apiService from './apiService';
import { renderFieldError } from './utils/formValidation';

const AddStaffView = ({ setCurrentView }) => {
    const { showAlert } = useAlert();
    const { addStaff } = useStaff();
    const [newStaff, setNewStaff] = useState({
        username: '',
        password: '',
        role: '',
        email: '',
        name: '',
        department: '',
        specialization: '',
        phone: '',
        hireDate: '',
        salary: '',
        workSchedule: '',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const [attemptedSubmit, setAttemptedSubmit] = useState(false);
    const [departments, setDepartments] = useState([]);

    // Fetch departments on mount
    useEffect(() => {
        fetchDepartments();
    }, []);

    const fetchDepartments = async () => {
        try {
            const depts = await apiService.getDepartments();
            setDepartments(depts);
        } catch (error) {
            console.error('Failed to fetch departments:', error);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewStaff(prev => ({ ...prev, [name]: value }));
    };

    // Helper to get input class names with validation
    const getInputClass = (fieldName) => {
        return `mt-1 block w-full border rounded-md shadow-sm p-2 focus:outline-none ${
            attemptedSubmit && validationErrors[fieldName]
                ? 'border-red-500 focus:ring-red-500 focus:border-red-500 bg-red-50'
                : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
        }`;
    };

    const validateForm = () => {
        const errors = {};
        
        if (!newStaff.username || newStaff.username.trim() === '') {
            errors.username = 'Username is required';
        }
        
        if (!newStaff.password || newStaff.password.trim() === '') {
            errors.password = 'Password is required';
        } else if (newStaff.password.length < 6) {
            errors.password = 'Password must be at least 6 characters';
        }
        
        if (!newStaff.role || newStaff.role === '') {
            errors.role = 'Role is required';
        }
        
        if (!newStaff.email || newStaff.email.trim() === '') {
            errors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newStaff.email)) {
            errors.email = 'Please enter a valid email address';
        }
        
        if (!newStaff.name || newStaff.name.trim() === '') {
            errors.name = 'Full name is required';
        }
        
        if (!newStaff.department || newStaff.department === '') {
            errors.department = 'Department is required';
        }
        
        if (!newStaff.phone || newStaff.phone.trim() === '') {
            errors.phone = 'Phone number is required';
        }
        
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleAddStaff = async (e) => {
        e.preventDefault();
        setAttemptedSubmit(true);
        
        if (!validateForm()) {
            showAlert('Please fill in all required fields marked in red', 'error');
            return;
        }

        setIsSubmitting(true);
        try {
            const userOrgId = localStorage.getItem('user_organization_id') ? 
                parseInt(localStorage.getItem('user_organization_id')) : null;

            if (!userOrgId) {
                throw new Error('No organization ID found. Please log in again.');
            }

            const staffData = {
                username: newStaff.username,
                password: newStaff.password,
                role: newStaff.role,
                email: newStaff.email,
                name: newStaff.name,
                department: newStaff.department,
                specialization: newStaff.specialization || null,
                phone: newStaff.phone,
                hireDate: newStaff.hireDate || null,
                salary: newStaff.salary ? parseFloat(newStaff.salary) : null,
                workSchedule: newStaff.workSchedule || null,
                organizationId: userOrgId,
            };

            await addStaff(staffData);

            showAlert(`Staff member added successfully! Username: ${newStaff.username}, Password: ${newStaff.password}`, 'success');
            setNewStaff({
                username: '',
                password: '',
                role: '',
                email: '',
                name: '',
                department: '',
                specialization: '',
                phone: '',
                hireDate: '',
                salary: '',
                workSchedule: '',
            });
            setCurrentView('staff');
        } catch (error) {
            console.error('Failed to add staff member:', error);
            showAlert(`Failed to add staff member: ${error.message}`, 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="space-y-6 bg-white p-6 rounded-lg shadow-md">
            <div>
                <h2 className="text-2xl font-bold text-gray-800">Add New Staff Member</h2>
                <p className="text-sm text-gray-600 mt-1">Create login credentials and give them to the staff member. They can change their password after first login.</p>
            </div>
            
            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                    <div className="text-blue-600 mt-0.5">ℹ️</div>
                    <div className="text-sm text-blue-800">
                        <p className="font-semibold mb-1">Password Management:</p>
                        <ul className="list-disc list-inside space-y-1 text-blue-700">
                            <li>Set a temporary password here</li>
                            <li>Give the username and password to the staff member physically</li>
                            <li>Staff can change their password after first login (Settings → Change Password)</li>
                        </ul>
                    </div>
                </div>
            </div>

            <form onSubmit={handleAddStaff} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label htmlFor="username" className="block text-sm font-medium text-gray-700">
                            Username <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            id="username"
                            name="username"
                            value={newStaff.username}
                            onChange={handleInputChange}
                            className={getInputClass('username')}
                            placeholder="e.g., dr.jane"
                        />
                        {renderFieldError(attemptedSubmit, 'username', validationErrors)}
                        {!validationErrors.username && <p className="text-xs text-gray-500 mt-1">Staff will use this to log in</p>}
                    </div>
                    <div>
                        <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                            Temporary Password <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            id="password"
                            name="password"
                            value={newStaff.password}
                            onChange={handleInputChange}
                            className={getInputClass('password')}
                            placeholder="Enter temporary password (min 6 characters)"
                        />
                        {renderFieldError(attemptedSubmit, 'password', validationErrors)}
                        {!validationErrors.password && <p className="text-xs text-gray-500 mt-1">Give this to the staff member - they can change it later</p>}
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label htmlFor="role" className="block text-sm font-medium text-gray-700">Role *</label>
                        <select
                            id="role"
                            name="role"
                            value={newStaff.role}
                            onChange={handleInputChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                            required
                        >
                            <option value="">Select Role</option>
                            <option value="doctor">Doctor</option>
                            <option value="nurse">Nurse</option>
                            <option value="receptionist">Receptionist</option>
                            <option value="pharmacist">Pharmacist</option>
                            <option value="cashier">Cashier</option>
                        </select>
                    </div>
                    <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email *</label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            value={newStaff.email}
                            onChange={handleInputChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                            required
                            placeholder="e.g., dr.jane@example.com"
                        />
                    </div>
                </div>
                <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">Full Name *</label>
                    <input
                        type="text"
                        id="name"
                        name="name"
                        value={newStaff.name}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                        placeholder="e.g., Dr. Jane Doe"
                    />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label htmlFor="department" className="block text-sm font-medium text-gray-700">Department *</label>
                        <select
                            id="department"
                            name="department"
                            value={newStaff.department}
                            onChange={handleInputChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                            required
                        >
                            <option value="">Select Department</option>
                            {departments.map((dept) => (
                                <option key={dept.id} value={dept.name}>
                                    {dept.name}
                                </option>
                            ))}
                        </select>
                        {departments.length === 0 && (
                            <p className="mt-1 text-xs text-orange-600">
                                No departments available. Please create departments in Settings first.
                            </p>
                        )}
                    </div>
                    <div>
                        <label htmlFor="specialization" className="block text-sm font-medium text-gray-700">Specialization</label>
                        <input
                            type="text"
                            id="specialization"
                            name="specialization"
                            value={newStaff.specialization}
                            onChange={handleInputChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="e.g., Cardiology"
                        />
                    </div>
                </div>
                <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700">Phone Number *</label>
                    <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={newStaff.phone}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                        placeholder="e.g., +254700001122"
                    />
                </div>
                <div>
                    <label htmlFor="hireDate" className="block text-sm font-medium text-gray-700">Hire Date</label>
                    <input
                        type="date"
                        id="hireDate"
                        name="hireDate"
                        value={newStaff.hireDate}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label htmlFor="salary" className="block text-sm font-medium text-gray-700">Salary (Optional)</label>
                        <input
                            type="number"
                            step="0.01"
                            id="salary"
                            name="salary"
                            value={newStaff.salary}
                            onChange={handleInputChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>
                    <div>
                        <label htmlFor="workSchedule" className="block text-sm font-medium text-gray-700">Work Schedule (Optional)</label>
                        <input
                            type="text"
                            id="workSchedule"
                            name="workSchedule"
                            value={newStaff.workSchedule}
                            onChange={handleInputChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="e.g., Mon-Fri 9-5"
                        />
                    </div>
                </div>
                <div className="flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={() => setCurrentView('staff')}
                        className="bg-gray-300 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-400 flex items-center gap-2"
                        disabled={isSubmitting}
                    >
                        <X className="w-4 h-4" />
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 flex items-center gap-2"
                        disabled={isSubmitting}
                    >
                        <Plus className="w-4 h-4" />
                        {isSubmitting ? 'Adding...' : 'Add Staff Member'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default AddStaffView;